import { useCallback, useEffect, useRef, useState } from 'react';
import { getSportsbookHistoryErrorMessage, requestSportsbookHistoryDetail } from '../api/sportsbookHistoryRequest.api.js';
import {
  createSportsbookHistoryDetailCache,
  sportsbookHistoryDetailCacheKey,
} from './sportsbookHistoryDetailCache.detail.js';

const IDLE_DETAIL_STATE = Object.freeze({ status: 'idle', detail: null, error: '' });

export const useSportsbookHistoryDetail = ({ service, rows, onVersionMismatch }) => {
  const [states, setStates] = useState({});
  const serviceRef = useRef(service);
  const versionMismatchRef = useRef(onVersionMismatch);
  serviceRef.current = service;
  versionMismatchRef.current = onVersionMismatch;

  const cacheRef = useRef(null);
  if (!cacheRef.current) {
    cacheRef.current = createSportsbookHistoryDetailCache({
      requestDetail: (historyId, signal) => requestSportsbookHistoryDetail({
        service: serviceRef.current,
        historyId,
        signal,
      }),
    });
  }

  useEffect(() => {
    cacheRef.current.syncRows(rows);
    const validKeys = new Map(rows.map((row) => [row.history_id, sportsbookHistoryDetailCacheKey(row)]));
    setStates((current) => {
      const next = Object.fromEntries(Object.entries(current).filter(([historyId, state]) => validKeys.get(historyId) === state.key));
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [rows]);

  useEffect(() => () => cacheRef.current.abortAll(), []);

  const load = useCallback(async (row, { force = false } = {}) => {
    const requestKey = sportsbookHistoryDetailCacheKey(row);
    const cached = cacheRef.current.peek(row);
    if (cached && !force) return cached;
    setStates((current) => ({
      ...current,
      [row.history_id]: { key: requestKey, status: 'loading', detail: null, error: '' },
    }));
    try {
      const detail = await cacheRef.current.load(row, { force });
      if (!detail) return null;
      const responseKey = sportsbookHistoryDetailCacheKey(detail);
      setStates((current) => ({
        ...current,
        [row.history_id]: { key: responseKey, status: 'ready', detail, error: '' },
      }));
      if (responseKey !== requestKey) versionMismatchRef.current?.();
      return detail;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      if (error?.code === 'SPORTSBOOK_HISTORY_DETAIL_VERSION_MISMATCH') {
        versionMismatchRef.current?.();
      }
      setStates((current) => {
        if (current[row.history_id]?.key !== requestKey) return current;
        return {
          ...current,
          [row.history_id]: {
            key: requestKey,
            status: 'error',
            detail: null,
            error: getSportsbookHistoryErrorMessage(error, '상세 내역을 불러오지 못했습니다.'),
          },
        };
      });
      return null;
    }
  }, []);

  const getState = useCallback((row) => {
    if (row === null || typeof row !== 'object' || Array.isArray(row)) return IDLE_DETAIL_STATE;
    const key = sportsbookHistoryDetailCacheKey(row);
    const cached = cacheRef.current.peek(row);
    if (cached) return { status: 'ready', detail: cached, error: '' };
    const state = states[row.history_id];
    return state?.key === key ? state : IDLE_DETAIL_STATE;
  }, [states]);

  return {
    load,
    refresh: useCallback((row) => load(row, { force: true }), [load]),
    getState,
    invalidate: useCallback((historyId) => cacheRef.current.invalidate(historyId), []),
  };
};
